/**
 * Het Universum: de grafiek van alle verbindingen. Post U deel 2, Frank 23 oktober 2026;
 * opnieuw opgezet naar het voorbeeld van Obsidian in GL198-U (24 oktober 2026).
 *
 * Alles hier is PUUR, zodat het op uitkomst te toetsen is: kleuren en paletten, grootte,
 * filters, buren, zoeken, de indeling, de namen die getoond worden, het raken van een stip
 * en de instellingen. Het venster (`components/UniversumVenster.jsx`) tekent alleen.
 *
 * DE INDELING IS EEN ECHTE KRACHTENINDELING, zoals d3 en Obsidian: elke stip stoot elke
 * andere af (Barnes-Hut, dus een groep ver weg telt als een stip), lijnen trekken, en een
 * centrale kracht houdt het geheel bij elkaar. De versie van 23 oktober stootte alleen af
 * binnen een vak van 48 punten; korte afstoting plus aantrekking naar het midden geeft een
 * kristalrooster, en dat was het raster van rijen en kolommen dat Frank op glas zag. Met
 * afstoting over de hele afstand ontstaan vanzelf wolken rond projecten en kennisbanken, en
 * drijven losse stippen naar de rand.
 */

export const SOORTEN = ["project", "bank", "document", "team", "gesprek", "agent", "meeting", "persoon",
  // Besluit Frank 24 okt 10:50: personen uit mail, agenda en TASQ, elk op een eigen knop.
  "mail", "afspraak", "taak"];

/** De soorten van voor 24 oktober: wie toen instellingen bewaarde, kende de nieuwe nog niet. */
export const OUDE_SOORTEN = ["project", "bank", "document", "team", "gesprek", "agent", "meeting", "persoon"];

/** De knoppen voor personen: een per bron, Fathom eerst zoals het was. */
export const PERSONENKNOPPEN = [
  { bron: "fathom", tekst: "Personen uit Fathom", testid: "universum-personen" },
  { bron: "mail", tekst: "Personen uit mail", testid: "universum-personen-mail" },
  { bron: "agenda", tekst: "Personen uit de agenda", testid: "universum-personen-agenda" },
  { bron: "tasq", tekst: "Personen uit TASQ", testid: "universum-personen-tasq" },
];

/** De Nederlandse naam per soort; het scherm vertaalt hem met t(). */
export const SOORT_TEKST = {
  project: "Projecten",
  bank: "Kennisbanken",
  document: "Documenten",
  team: "Teams",
  gesprek: "Gesprekken",
  agent: "Agenten",
  meeting: "Meetings",
  persoon: "Personen",
  mail: "Mails",
  afspraak: "Afspraken",
  taak: "Taken",
};

/** Warme NovaMund-kleuren per soort, op de donkere achtergrond. */
export const KLEUR = {
  project: "#FF6B33",
  bank: "#FFB347",
  document: "#C9B8A6",
  team: "#A47CFF",
  gesprek: "#FF9A52",
  agent: "#F07890",
  meeting: "#E0BC3A",
  persoon: "#F7E3C6",
  mail: "#7FC8F8",
  afspraak: "#8FD694",
  taak: "#F2A65A",
};

/** Dezelfde soorten, donkerder, voor de lichte achtergrond. */
export const KLEUR_LICHT = {
  project: "#C2410C",
  bank: "#B45309",
  document: "#7A6450",
  team: "#5B21B6",
  gesprek: "#C2410C",
  agent: "#B4234A",
  meeting: "#8A6D0B",
  persoon: "#6B5A48",
  mail: "#1D5F8A",
  afspraak: "#2F6B34",
  taak: "#9A4A0C",
};

/**
 * Twee paletten. Op licht zijn stippen en lijnen DONKERDER en niet dezelfde kleur met een
 * andere achtergrond, anders verdwijnt een beige stip in een beige vlak.
 */
export const PALET = {
  donker: {
    soort: KLEUR, lijn: "rgba(190, 178, 166, 0.34)", lijnDof: "rgba(190, 178, 166, 0.06)",
    lijnFel: "#FFB347", naam: "#EDE5DC", rand: "#FFFFFF", spook: "#6E655D", verzadiging: 72, licht: 62,
  },
  licht: {
    soort: KLEUR_LICHT, lijn: "rgba(60, 48, 40, 0.30)", lijnDof: "rgba(60, 48, 40, 0.06)",
    lijnFel: "#C2410C", naam: "#2A1F18", rand: "#1A1410", spook: "#B3A89E", verzadiging: 70, licht: 36,
  },
};

/** Tinten die op het oog uit elkaar liggen, zodat twee clusters naast elkaar te onderscheiden zijn. */
const TINTEN = [16, 200, 44, 280, 150, 330, 92, 230, 62, 180, 304, 120, 0, 258];

/** De vaste kleur van een cluster (project of kennisbank), per palet. PUUR. */
export function clusterKleur(sleutel, licht = false) {
  const p = licht ? PALET.licht : PALET.donker;
  if (!sleutel) return p.soort.document;
  let h = 0;
  for (let i = 0; i < sleutel.length; i += 1) h = (h * 31 + sleutel.charCodeAt(i)) >>> 0;
  return `hsl(${TINTEN[h % TINTEN.length]}, ${p.verzadiging}%, ${p.licht}%)`;
}

/**
 * Het cluster per stip: het project waar hij onder valt, anders zijn kennisbank. Een
 * document zonder project kleurt zo naar zijn eigen bank in plaats van naar niets. PUUR.
 */
export function clusters(graaf) {
  const bank = new Map();
  for (const l of graaf?.lijnen || []) if (l.soort === "document_in_bank") bank.set(l.van, l.naar);
  const uit = new Map();
  for (const s of graaf?.stippen || []) {
    if (String(s.groep || "").startsWith("project:")) uit.set(s.id, s.groep);
    else if (s.soort === "bank") uit.set(s.id, s.id);
    else uit.set(s.id, bank.get(s.id) || s.groep || null);
  }
  return uit;
}

/**
 * De kleur van een stip. Documenten kleuren naar hun cluster, zodat je de wolken ziet; de
 * andere soorten houden hun soortkleur. Een spookstip is grijs. PUUR.
 */
export function kleurVan(stip, cluster, licht = false) {
  const p = licht ? PALET.licht : PALET.donker;
  if (stip.bestaat === false) return p.spook;
  if (stip.soort !== "document") return p.soort[stip.soort] || p.soort.document;
  return clusterKleur(cluster, licht);
}

/**
 * De straal van een stip in wereldpunten: klein, en groter naarmate hij meer verbindingen
 * heeft, met een klein maximum. Een document met een verbinding is 2,35; een bank met 490
 * documenten blijft op 9. `grootte` is de schuifregelaar Stipgrootte. PUUR.
 */
export const STRAAL_MAX = 9;
export function straal(graad, grootte = 1) {
  return grootte * Math.min(STRAAL_MAX, 2 + 0.35 * Math.sqrt(Math.max(0, graad || 0)));
}

/**
 * De straal op het scherm. Inzoomen maakt een stip maar 0,4 keer zo snel groter als de rest,
 * zodat hij bij sterk inzoomen geen schijf wordt die zijn buren bedekt; uitzoomen maakt hem
 * nooit kleiner dan `SCHERM_MIN`, want bij 3353 stippen past het geheel pas op een kwart van
 * de schaal, en dan was een document een halve pixel (gemeten op glas, 24 oktober). PUUR.
 */
export const SCHERM_MIN = 1.5;
export function schermStraal(r, schaal) {
  return Math.max(Math.min(SCHERM_MIN, r), schaal <= 1 ? r * schaal : r * (1 + (schaal - 1) * 0.4));
}

/**
 * De stippen en lijnen die na de filters overblijven. PUUR.
 * `soorten`: Set van soorten die aan staan. `groep`: "" of een groep-id. `alleenBestaand`:
 * spookstippen weg. `losse`: false haalt stippen zonder ZICHTBARE verbinding weg.
 * Een lijn blijft alleen als beide uiteinden zichtbaar zijn.
 */
export function gefilterd(graaf, { soorten, groep = "", alleenBestaand = false, losse = true } = {}) {
  const aan = soorten || new Set(SOORTEN);
  const inGroep = groepFilter(graaf, groep);
  let stippen = (graaf?.stippen || []).filter((s) => aan.has(s.soort)
    && (!alleenBestaand || s.bestaat !== false)
    && inGroep(s));
  const ids = new Set(stippen.map((s) => s.id));
  const lijnen = (graaf?.lijnen || []).filter((l) => ids.has(l.van) && ids.has(l.naar));
  if (!losse) {
    const verbonden = new Set();
    for (const l of lijnen) { verbonden.add(l.van); verbonden.add(l.naar); }
    stippen = stippen.filter((s) => verbonden.has(s.id));
  }
  return { stippen, lijnen };
}

/** De buren van een stip: de stippen aan de andere kant van zijn lijnen. PUUR. */
export function buren(lijnen, id) {
  const uit = new Set();
  for (const l of lijnen || []) {
    if (l.van === id) uit.add(l.naar);
    else if (l.naar === id) uit.add(l.van);
  }
  return uit;
}

/** De stippen waarvan de naam de zoekterm bevat, zonder hoofdletters. PUUR. */
export function zoek(stippen, term) {
  const t = String(term || "").trim().toLowerCase();
  if (!t) return [];
  return (stippen || []).filter((s) => String(s.naam || "").toLowerCase().includes(t)).map((s) => s.id);
}

/**
 * Wanneer twee namen dezelfde naam zijn: zonder randspaties, dubbele spaties en hoofdletters, en
 * zonder een slot-s (vanaf vier tekens). PUUR. GELIJK AAN `backend/services/naamsleutel.py`; een toets
 * aan beide kanten met dezelfde paren houdt ze gelijk. "SPEAQ" en "SPEAQ ID" blijven apart.
 */
export function naamSleutel(naam) {
  const kaal = String(naam || "").split(/\s+/).filter(Boolean).join(" ").toLowerCase();
  return kaal.length > 3 && kaal.endsWith("s") ? kaal.slice(0, -1) : kaal;
}

/**
 * De groepen voor het filter: teams en projecten die als stip bestaan, op naam, en ELKE NAAM EEN KEER
 * (vondst 3 na de glasronde van 0.9.202: FODIQ stond naast FoDiQ). Een naam die alleen voorkomt houdt
 * het id van zijn stip; een naam met een team EN een project wordt `naam:<sleutel>` met de leden erbij.
 * De weergavenaam is die van het project, want daar staat het werk. PUUR.
 */
export function groepen(graaf) {
  const per = new Map();
  for (const s of graaf?.stippen || []) {
    if (s.soort !== "team" && s.soort !== "project") continue;
    const k = naamSleutel(s.naam);
    if (!per.has(k)) per.set(k, []);
    per.get(k).push(s);
  }
  return [...per.entries()].map(([k, leden]) => {
    const naam = (leden.find((l) => l.soort === "project") || leden[0]).naam.trim();
    return leden.length === 1
      ? { id: leden[0].id, naam, soort: leden[0].soort, leden: [leden[0].id] }
      : { id: `naam:${k}`, naam, soort: "naam", leden: leden.map((l) => l.id) };
  }).sort((a, b) => a.naam.localeCompare(b.naam));
}

/**
 * Wie er in een gekozen groep hoort. PUUR. Een gewone groep: de stip zelf en wat erin zit. Een
 * samengevoegde naam: de leden, wat erin zit, de kennisbanken met dezelfde naam en hun documenten.
 */
export function groepFilter(graaf, groep) {
  if (!groep) return () => true;
  if (!groep.startsWith("naam:")) return (s) => s.groep === groep || s.id === groep;
  const k = groep.slice(5);
  const leden = new Set((groepen(graaf).find((g) => g.id === groep) || { leden: [] }).leden);
  const banken = new Set((graaf?.stippen || []).filter((s) => s.soort === "bank"
    && (leden.has(s.groep) || naamSleutel(s.naam) === k)).map((s) => s.id));
  const docs = new Set((graaf?.lijnen || []).filter((l) => l.soort === "document_in_bank" && banken.has(l.naar))
    .map((l) => l.van));
  return (s) => leden.has(s.id) || leden.has(s.groep) || banken.has(s.id) || docs.has(s.id);
}

/** Waar een stip heen gaat als je hem opent, binnen de bestaande schermen. PUUR. */
export function adresVan(stip) {
  // Een spookstip is een naam waar geen document bij hoort; er is niets om te openen.
  if (!stip || stip.bestaat === false) return null;
  switch (stip.soort) {
    case "gesprek": return `/chat/${stip.ref}`;
    case "project": return `/project/${stip.ref}`;
    case "bank":
    case "document": return "/knowledge";
    case "agent": return "/agents";
    case "team": return "/teams";
    // Deel 3: een meeting opent in Fathom zelf; een persoon heeft geen eigen scherm.
    case "meeting": return /^https:\/\//.test(stip.url || "") ? stip.url : null;
    // Een taak opent in TASQ zelf; een mail of afspraak heeft hier geen eigen adres.
    case "taak": return /^https:\/\//.test(stip.url || "") ? stip.url : null;
    default: return null;
  }
}

// ---------------------------------------------------------------------------------------
// DE INSTELLINGEN, zoals het paneel van Obsidian: Filters, Weergave en Krachten.
// ---------------------------------------------------------------------------------------

/** De standaard; de knop Standaard zet alles hierop terug. */
export const STANDAARD = Object.freeze({
  paneel: true,
  blokFilters: true,
  blokWeergave: true,
  blokKrachten: false,
  labels: true,
  alleenBestaand: false,
  losse: true,
  soorten: SOORTEN,
  // Welke soorten er bestonden toen dit werd bewaard: een NIEUWE soort staat daarna aan.
  bekend: SOORTEN,
  pijlen: false,
  drempel: 1,
  stipgrootte: 1,
  lijndikte: 1,
  centraal: 0.5,
  afstoting: 10,
  lijnkracht: 1,
  lijnafstand: 30,
});

/** Het bereik van elke schuifregelaar: [laagste, hoogste, stap]. */
export const BEREIK = Object.freeze({
  drempel: [0, 3, 0.1],
  stipgrootte: [0.3, 3, 0.1],
  lijndikte: [0.2, 4, 0.1],
  centraal: [0, 1, 0.01],
  afstoting: [0, 20, 0.5],
  lijnkracht: [0, 1, 0.01],
  lijnafstand: [5, 200, 1],
});

const SCHAKELAARS = ["paneel", "blokFilters", "blokWeergave", "blokKrachten", "labels", "alleenBestaand", "losse", "pijlen"];

/** Een bewaarde of gewijzigde set instellingen, gecontroleerd en binnen bereik. PUUR. */
export function geldig(ruw) {
  const uit = { ...STANDAARD };
  const r = ruw && typeof ruw === "object" ? ruw : {};
  for (const k of SCHAKELAARS) if (typeof r[k] === "boolean") uit[k] = r[k];
  for (const [k, [lo, hi]] of Object.entries(BEREIK)) {
    const v = typeof r[k] === "number" || typeof r[k] === "string" ? Number(r[k]) : NaN;
    if (Number.isFinite(v)) uit[k] = Math.min(hi, Math.max(lo, v));
  }
  if (Array.isArray(r.soorten)) {
    // Wat iemand uitzette blijft uit; een soort die bij het bewaren nog niet bestond, staat aan.
    const bekend = Array.isArray(r.bekend) ? r.bekend : OUDE_SOORTEN;
    uit.soorten = SOORTEN.filter((s) => r.soorten.includes(s) || !bekend.includes(s));
  }
  uit.bekend = SOORTEN;
  return uit;
}

/** De sleutel waaronder een gebruiker zijn instellingen bewaart: per gebruiker een eigen. PUUR. */
export function opslagSleutel(gebruiker) {
  return `novamund.universum.v1.${gebruiker || "anoniem"}`;
}

/** De bewaarde instellingen van deze gebruiker, of de standaard. Gooit nooit. */
export function leesInstellingen(opslag, gebruiker) {
  try {
    const ruw = opslag?.getItem(opslagSleutel(gebruiker));
    return geldig(ruw ? JSON.parse(ruw) : null);
  } catch {
    return geldig(null);
  }
}

/** Bewaar de instellingen van deze gebruiker. Gooit nooit (privevenster, volle opslag). */
export function bewaarInstellingen(opslag, gebruiker, instellingen) {
  try {
    opslag?.setItem(opslagSleutel(gebruiker), JSON.stringify(geldig(instellingen)));
    return true;
  } catch {
    return false;
  }
}

/**
 * Van de schuifregelaars naar de krachten. De standaard komt uit op die van d3 (afstoting
 * -30, lijnafstand 30). PUUR.
 */
export function krachten(inst = STANDAARD) {
  return {
    centrum: 0.005 + 0.15 * inst.centraal,
    afstoting: -3 * inst.afstoting,
    lijn: inst.lijnkracht,
    afstand: inst.lijnafstand,
  };
}

// ---------------------------------------------------------------------------------------
// DE INDELING
// ---------------------------------------------------------------------------------------

export const MAX_STAPPEN = 600;
export const ALFA_MIN = 0.001;
const ALFA_VERVAL = 1 - Math.pow(ALFA_MIN, 1 / 300);
const SNELHEID_VERVAL = 0.4;
export const THETA2 = 0.81;
const AFSTAND_MIN2 = 1;

/**
 * Een beginindeling: een zonnebloemspiraal zoals in d3, zodat de uitkomst vastligt. Een
 * stip die er al was houdt zijn plek (`vorige`: Map id -> [x, y]), zodat een filter niet
 * het hele universum opnieuw laat ontploffen. PUUR.
 */
export function maakIndeling(stippen, lijnen, vorige = null) {
  const n = stippen.length;
  const index = new Map(stippen.map((s, i) => [s.id, i]));
  const x = new Float32Array(n);
  const y = new Float32Array(n);
  const hoek = Math.PI * (3 - Math.sqrt(5));
  let bewaard = 0;
  for (let i = 0; i < n; i += 1) {
    const oud = vorige?.get(stippen[i].id);
    if (oud) { [x[i], y[i]] = oud; bewaard += 1; continue; }
    const r = 10 * Math.sqrt(0.5 + i);
    x[i] = r * Math.cos(i * hoek);
    y[i] = r * Math.sin(i * hoek);
  }
  const paren = [];
  const telling = new Uint32Array(n);
  for (const l of lijnen) {
    const a = index.get(l.van);
    const b = index.get(l.naar);
    if (a !== undefined && b !== undefined && a !== b) { paren.push(a, b); telling[a] += 1; telling[b] += 1; }
  }
  return {
    n, x, y, vx: new Float32Array(n), vy: new Float32Array(n), paren: Int32Array.from(paren), telling,
    // Waren bijna alle stippen er al, dan is het een kleine bijstelling en geen nieuwe start.
    alfa: n > 0 && bewaard / n > 0.9 ? 0.3 : 1, stap: 0,
  };
}

/** De plekken van een indeling per stip-id, voor `maakIndeling(..., vorige)`. PUUR. */
export function plekken(ind, stippen) {
  const uit = new Map();
  if (!ind) return uit;
  for (let i = 0; i < ind.n && i < stippen.length; i += 1) uit.set(stippen[i].id, [ind.x[i], ind.y[i]]);
  return uit;
}

/** Zet de indeling weer in beweging, bijvoorbeeld na een schuifregelaar. PUUR op de indeling. */
export function verwarm(ind, alfa = 0.3) {
  ind.alfa = Math.max(ind.alfa, alfa);
  ind.stap = 0;
}

/**
 * Een quadtree over alle stippen, in vaste rijen getallen in plaats van objecten, want hij
 * wordt elke stap opnieuw gebouwd. Per knoop: zwaartepunt, massa, de grootte van het vak,
 * en de eerste stip als het een blad is (met een keten voor stippen op dezelfde plek).
 */
function bouwBoom(ind, vorig) {
  const { n, x, y } = ind;
  let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
  for (let i = 0; i < n; i += 1) {
    if (x[i] < x0) x0 = x[i];
    if (x[i] > x1) x1 = x[i];
    if (y[i] < y0) y0 = y[i];
    if (y[i] > y1) y1 = y[i];
  }
  const grootte = Math.max(x1 - x0, y1 - y0, 1) * 1.0001;
  const max = 4 * n + 64;
  const boom = vorig && vorig.max >= max ? vorig : {
    max, kind: new Int32Array(max * 4), blad: new Int32Array(max), keten: new Int32Array(Math.max(n, 1)),
    bx: new Float64Array(max), by: new Float64Array(max), bs: new Float64Array(max),
    mx: new Float64Array(max), my: new Float64Array(max), massa: new Float64Array(max),
    stapel: new Int32Array(max),
  };
  const { kind, blad, keten, bx, by, bs } = boom;
  let aantal = 0;
  const nieuw = (ox, oy, s) => {
    const k = aantal; aantal += 1;
    kind[4 * k] = -1; kind[4 * k + 1] = -1; kind[4 * k + 2] = -1; kind[4 * k + 3] = -1;
    blad[k] = -1; bx[k] = ox; by[k] = oy; bs[k] = s;
    return k;
  };
  nieuw(x0, y0, grootte);
  const vakVan = (k, px, py) => {
    const h = bs[k] / 2;
    return (px >= bx[k] + h ? 1 : 0) + (py >= by[k] + h ? 2 : 0);
  };
  const kindVan = (k, q) => {
    let c = kind[4 * k + q];
    if (c < 0) {
      const h = bs[k] / 2;
      c = nieuw(bx[k] + (q & 1 ? h : 0), by[k] + (q & 2 ? h : 0), h);
      kind[4 * k + q] = c;
    }
    return c;
  };
  const intern = (k) => kind[4 * k] >= 0 || kind[4 * k + 1] >= 0 || kind[4 * k + 2] >= 0 || kind[4 * k + 3] >= 0;
  for (let i = 0; i < n; i += 1) {
    keten[i] = -1;
    let k = 0;
    for (let diepte = 0; ; diepte += 1) {
      if (intern(k)) { k = kindVan(k, vakVan(k, x[i], y[i])); continue; }
      const j = blad[k];
      if (j < 0) { blad[k] = i; break; }
      // Een bezet blad: zelfde plek of te diep, dan aan de keten; anders splitsen.
      if (diepte > 40 || (x[j] === x[i] && y[j] === y[i])) { keten[i] = keten[j]; keten[j] = i; break; }
      blad[k] = -1;
      blad[kindVan(k, vakVan(k, x[j], y[j]))] = j;
      k = kindVan(k, vakVan(k, x[i], y[i]));
    }
  }
  // Zwaartepunten van onder naar boven: een kind is altijd later gemaakt dan zijn ouder.
  const { mx, my, massa } = boom;
  for (let k = aantal - 1; k >= 0; k -= 1) {
    let m = 0; let sx = 0; let sy = 0;
    if (blad[k] >= 0) {
      for (let j = blad[k]; j >= 0; j = keten[j]) { m += 1; sx += x[j]; sy += y[j]; }
    } else {
      for (let q = 0; q < 4; q += 1) {
        const c = kind[4 * k + q];
        if (c >= 0 && massa[c] > 0) { m += massa[c]; sx += mx[c] * massa[c]; sy += my[c] * massa[c]; }
      }
    }
    massa[k] = m; mx[k] = m ? sx / m : 0; my[k] = m ? sy / m : 0;
  }
  boom.aantal = aantal;
  return boom;
}

/**
 * Een stap van de indeling, zoals een tik van d3-force: afstoting (Barnes-Hut), lijnen,
 * centrale kracht, dan bewegen met demping. Geeft de grootste verplaatsing terug. PUUR op de
 * indeling (die wordt bijgewerkt). `theta2` 0 rekent elk paar door; alleen voor de toets.
 */
export function stap(ind, k = krachten(), { theta2 = THETA2 } = {}) {
  const { n, x, y, vx, vy, paren, telling } = ind;
  if (n === 0) { ind.alfa = 0; return 0; }
  const alfa = ind.alfa;
  // AFSTOTING over de hele afstand: een vak dat ver genoeg weg is telt als een stip.
  if (k.afstoting !== 0) {
    const boom = bouwBoom(ind, ind.boom);
    ind.boom = boom;
    const { kind, blad, keten, bs, mx, my, massa, stapel } = boom;
    const sterk = k.afstoting * alfa;
    for (let i = 0; i < n; i += 1) {
      let top = 0;
      stapel[top++] = 0;
      let fx = 0; let fy = 0;
      while (top > 0) {
        const q = stapel[--top];
        if (massa[q] === 0) continue;
        let dx = mx[q] - x[i];
        let dy = my[q] - y[i];
        let l = dx * dx + dy * dy;
        const s = bs[q];
        if (blad[q] < 0) {
          if (s * s < theta2 * l) {
            if (l < AFSTAND_MIN2) l = Math.sqrt(AFSTAND_MIN2 * l) || AFSTAND_MIN2;
            fx += (dx * sterk * massa[q]) / l; fy += (dy * sterk * massa[q]) / l;
          } else {
            for (let c = 0; c < 4; c += 1) if (kind[4 * q + c] >= 0) stapel[top++] = kind[4 * q + c];
          }
          continue;
        }
        for (let j = blad[q]; j >= 0; j = keten[j]) {
          if (j === i) continue;
          dx = x[j] - x[i]; dy = y[j] - y[i]; l = dx * dx + dy * dy;
          // Twee stippen op dezelfde plek: een vaste kleine duw, geen toeval.
          if (l === 0) { dx = (((i * 7 + j) % 11) - 5 || 1) * 1e-3; dy = (((i + j * 3) % 7) - 3) * 1e-3; l = dx * dx + dy * dy; }
          if (l < AFSTAND_MIN2) l = Math.sqrt(AFSTAND_MIN2 * l);
          fx += (dx * sterk) / l; fy += (dy * sterk) / l;
        }
      }
      vx[i] += fx; vy[i] += fy;
    }
  }
  // LIJNEN, met de sterkte en het evenwicht van d3: een lijn naar een drukke stip trekt
  // vooral de rustige kant, zodat een bank met 490 documenten niet van zijn plek wordt gesleurd.
  if (k.lijn > 0) {
    for (let p = 0; p < paren.length; p += 2) {
      const a = paren[p];
      const b = paren[p + 1];
      let dx = x[b] + vx[b] - x[a] - vx[a];
      let dy = y[b] + vy[b] - y[a] - vy[a];
      if (dx === 0 && dy === 0) { dx = 1e-3; dy = 1e-3; }
      const l = Math.sqrt(dx * dx + dy * dy);
      const f = ((l - k.afstand) / l) * alfa * (k.lijn / Math.min(telling[a], telling[b]));
      dx *= f; dy *= f;
      const bias = telling[a] / (telling[a] + telling[b]);
      vx[b] -= dx * bias; vy[b] -= dy * bias;
      vx[a] += dx * (1 - bias); vy[a] += dy * (1 - bias);
    }
  }
  // CENTRALE KRACHT, dan bewegen met demping.
  let grootste = 0;
  const c = k.centrum * alfa;
  for (let i = 0; i < n; i += 1) {
    vx[i] -= x[i] * c; vy[i] -= y[i] * c;
    vx[i] *= 1 - SNELHEID_VERVAL; vy[i] *= 1 - SNELHEID_VERVAL;
    x[i] += vx[i]; y[i] += vy[i];
    const v = Math.abs(vx[i]) + Math.abs(vy[i]);
    if (v > grootste) grootste = v;
  }
  ind.alfa -= ind.alfa * ALFA_VERVAL;
  ind.stap += 1;
  return grootste;
}

/** Is de indeling klaar: afgekoeld, of het maximum aantal stappen gedaan. PUUR. */
export function klaar(ind) {
  return ind.n === 0 || ind.alfa < ALFA_MIN || ind.stap >= MAX_STAPPEN;
}

/** De schaal en verschuiving waarmee alle stippen in het scherm passen. PUUR. */
export function passend(ind, breedte, hoogte, marge = 0.9, rechts = 0) {
  if (!ind?.n || !breedte || !hoogte) return { schaal: 1, tx: 0, ty: 0 };
  // `rechts`: zoveel punten aan de rechterkant zijn bedekt (het paneel); pas in de rest.
  const vrij = Math.max(breedte - rechts, breedte / 2);
  let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
  for (let i = 0; i < ind.n; i += 1) {
    if (ind.x[i] < x0) x0 = ind.x[i];
    if (ind.x[i] > x1) x1 = ind.x[i];
    if (ind.y[i] < y0) y0 = ind.y[i];
    if (ind.y[i] > y1) y1 = ind.y[i];
  }
  const schaal = Math.min(4, Math.max(0.05,
    marge * Math.min(vrij / Math.max(x1 - x0, 1), hoogte / Math.max(y1 - y0, 1))));
  return { schaal, tx: -((x0 + x1) / 2) * schaal - (breedte - vrij) / 2, ty: -((y0 + y1) / 2) * schaal };
}

/** De stip onder een punt in wereldcoordinaten, of -1. De bovenste (laatste) wint. PUUR. */
export function geraakt(ind, stralen, wx, wy, marge = 2) {
  for (let i = ind.n - 1; i >= 0; i -= 1) {
    const dx = ind.x[i] - wx;
    const dy = ind.y[i] - wy;
    const r = stralen[i] + marge;
    if (dx * dx + dy * dy <= r * r) return i;
  }
  return -1;
}

/**
 * Of een stip een naam mag dragen, voor de botsingstoets. Gekozen, gezocht of aangewezen
 * altijd; verder alleen met Labels aan en een stip die groot genoeg is: straal `r` (in
 * wereldpunten) maal de zoom, maar uitzoomen telt niet mee. Bij drempel 1 zijn dat zonder
 * zoom alleen de grootste stippen (straal vanaf 6: banken en projecten met tientallen
 * verbindingen); inzoomen of de drempel verlagen toont er meer. PUUR.
 */
export function wilNaam({ labels, drempel }, r, schaal = 1, bijzonder = false) {
  if (bijzonder) return true;
  if (!labels) return false;
  return r * Math.max(1, schaal) >= 6 * drempel;
}

/**
 * GL199-1: de rand om een naam. De tekst krijgt een halo van 3 punten (1,5 aan elke kant) en
 * er hoort lucht tussen twee namen; zonder die rand raakten de halo's elkaar en lagen namen
 * rond een drukke stip deels over elkaar, vooral zichtbaar in de donkere modus.
 */
export const NAAM_RAND = 2;

/**
 * Het vak van een naam zoals de browser hem tekent: de GEMETEN breedte (measureText), niet een
 * schatting per letter. Gemeten in Chromium op 11 punten: hoofdletters zijn tot 47 procent
 * breder dan de oude schatting van 0,56 maal de lettergrootte. De hoogte is de letterkast
 * (boven- plus onderlengte, 13 op 11 punten). PUUR.
 */
export function naamVak(breedte, letter) {
  return { b: breedte + 2 * NAAM_RAND, h: Math.ceil(letter * 1.2) + 2 * NAAM_RAND };
}

/**
 * GL199-1: waar een naam naast zijn stip mag staan, in volgorde van voorkeur: rechts, links,
 * boven, onder. Midden van de stip op (sx, sy), schermstraal sr, vak b bij h. PUUR.
 */
export function naamPlekken(sx, sy, sr, b, h) {
  return [
    { x: sx + sr + 1, y: sy - h / 2 },
    { x: sx - sr - 1 - b, y: sy - h / 2 },
    { x: sx - b / 2, y: sy - sr - 1 - h },
    { x: sx - b / 2, y: sy + sr + 1 },
  ];
}

/**
 * Welke namen er getoond worden en waar, zonder dat er twee over elkaar heen vallen.
 * `kandidaten` zijn vakken op het scherm ({i, x, y, b, h}, eventueel met `alt`: andere plekken
 * {x, y} voor hetzelfde vak) in volgorde van belang. De eerste wint; een naam die op zijn plek
 * een al geplaatste raakt, probeert zijn andere plekken en valt pas weg als die ook botsen.
 * Een raster van 64 punten houdt het snel. Geeft [{i, x, y}]. PUUR.
 */
export function plaatsNamen(kandidaten, max = 250) {
  const VAKJE = 64;
  const raster = new Map();
  const geplaatst = [];
  const botst = (x, y, b, h) => {
    const gx0 = Math.floor(x / VAKJE); const gx1 = Math.floor((x + b) / VAKJE);
    const gy0 = Math.floor(y / VAKJE); const gy1 = Math.floor((y + h) / VAKJE);
    for (let gx = gx0; gx <= gx1; gx += 1) {
      for (let gy = gy0; gy <= gy1; gy += 1) {
        for (const o of raster.get(`${gx},${gy}`) || []) {
          if (x < o.x + o.b && o.x < x + b && y < o.y + o.h && o.y < y + h) return true;
        }
      }
    }
    return false;
  };
  for (const k of kandidaten) {
    if (geplaatst.length >= max) break;
    const plek = [{ x: k.x, y: k.y }, ...(k.alt || [])].find((p) => !botst(p.x, p.y, k.b, k.h));
    if (!plek) continue;
    const vak = { x: plek.x, y: plek.y, b: k.b, h: k.h };
    geplaatst.push({ i: k.i, x: plek.x, y: plek.y });
    const gx0 = Math.floor(vak.x / VAKJE); const gx1 = Math.floor((vak.x + vak.b) / VAKJE);
    const gy0 = Math.floor(vak.y / VAKJE); const gy1 = Math.floor((vak.y + vak.h) / VAKJE);
    for (let gx = gx0; gx <= gx1; gx += 1) {
      for (let gy = gy0; gy <= gy1; gy += 1) {
        const sl = `${gx},${gy}`;
        const lijst = raster.get(sl);
        if (lijst) lijst.push(vak); else raster.set(sl, [vak]);
      }
    }
  }
  return geplaatst;
}

/** Welke namen er getoond worden: de nummers uit `plaatsNamen`, op hun eerste plek. PUUR. */
export function kiesNamen(kandidaten, max = 250) {
  return plaatsNamen(kandidaten.map(({ alt, ...k }) => k), max).map((p) => p.i);
}

/** Deel 3: nieuwe stippen en lijnen erbij, zonder dubbele. PUUR. */
export function samengevoegd(graaf, erbij) {
  const ids = new Set((graaf?.stippen || []).map((s) => s.id));
  const nieuw = (erbij?.stippen || []).filter((s) => !ids.has(s.id));
  const sleutel = (l) => `${l.van}|${l.naar}`;
  const al = new Set((graaf?.lijnen || []).map(sleutel));
  const lijnen = (erbij?.lijnen || []).filter((l) => !al.has(sleutel(l)));
  const alleLijnen = [...(graaf?.lijnen || []), ...lijnen];
  // Deel 4: DE GRAAD OPNIEUW TELLEN, want verwijzingen geven bestaande stippen er lijnen bij.
  const graad = new Map();
  for (const l of alleLijnen) {
    graad.set(l.van, (graad.get(l.van) || 0) + 1);
    graad.set(l.naar, (graad.get(l.naar) || 0) + 1);
  }
  const stippen = [...(graaf?.stippen || []), ...nieuw].map((s) => ({ ...s, graad: graad.get(s.id) || 0 }));
  return { ...graaf, stippen, lijnen: alleLijnen };
}
